import styled, { css } from 'styled-components';
import { mix, rgba } from 'polished';
import { FieldValidationState } from '@cld/common-definitions';

import theme, { combineValues, ThemeApi } from '@cld/theme-helper';
import { typographyMixin } from '@cld/typography';
import { supportFieldLineLayout } from '@cld/field-inline-layout';
import { FieldSize } from '../FieldSize';

const t = theme({ basePath: 'components.text-field' });

export interface FieldBaseProps {
  isDisabled: boolean;
  hasLeadingDecoration: boolean;
  hasTrailingDecoration: boolean;
  isFocused: boolean;
  size: FieldSize;
  validationState: FieldValidationState;
}

const validationStateBorderColorMap: Map<FieldValidationState, ThemeApi> = new Map([
  ['error', t`border.color.error`],
  ['none', t`border.color.base`],
  ['warning', t`border.color.warning`],
  ['success', t`border.color.success`],
]);

export const FieldBase = styled.div<FieldBaseProps>`
  ${(props) => typographyMixin(props.size, 'regular')};
  align-items: center;
  background-color: ${t`background.color.base`};
  border: ${t`border.width`} solid ${({ validationState }) => validationStateBorderColorMap.get(validationState)};
  border-radius: ${t`border.radius`};
  overflow: hidden;
  ${supportFieldLineLayout()};
  color: ${t`color.base`};
  display: flex;
  transition: border-color 250ms, box-shadow 250ms;

  ${({ isFocused }) =>
    isFocused &&
    css`
      border-color: ${t`border.color.focus`};
      box-shadow: 0 0 0 3px ${combineValues(t`outline.color.base`, (color) => rgba(color, 0.5))};
    `}

  ${({ isDisabled }) =>
    isDisabled
      ? css`
          background-color: ${combineValues(t`background.color.base`, theme`palette.contrastInvert`, (baseColor, contrastColor) =>
            mix(0.5, baseColor, contrastColor)
          )};
          border-color: ${combineValues(t`border.color.base`, theme`palette.contrastInvert`, (baseColor, contrastColor) =>
            mix(0.5, baseColor, contrastColor)
          )};
          color: ${combineValues(t`color.base`, theme`palette.contrastInvert`, (baseColor, contrastColor) => mix(0.5, baseColor, contrastColor))};
        `
      : css`
          &:hover {
            border-color: ${t`border.color.hover`};
          }
        `}

  > input {
    ${(props) => typographyMixin(props.size, 'regular')};
    background: none;
    border: none;
   
    color: ${t`input.color.base`};
    height: 100%;
    padding: ${t`input.padding.${'size'}`};
    width: 100%;

    ${({ hasLeadingDecoration }) =>
      hasLeadingDecoration &&
      css`
        padding-left: ${t`input.decorationMargin`};
      `}

    ${({ hasTrailingDecoration }) =>
      hasTrailingDecoration &&
      css`
        padding-right: ${t`input.decorationMargin`};
      `}

    &:disabled,
    &::placeholder {
      color: ${t`input.color.placeholder`};
    }

    &:focus {
      outline: none;
    }
           
    //hide (chrome) browser's auto-complete background
    box-shadow: 0 0 0 100px ${t`background.color.base`} inset;    	
  }
`;
